import { useMemo } from 'react';

import meanBy from 'lodash/meanBy';
import round from 'lodash/round';
import {
  ChartMetricsRow,
  ChartMetricsRowSkeleton,
} from '@cast/features-cluster-ui';

import { InventoryResourceMetricBox } from '../../../../_components/ui/InventoryResourceMetricBox';
import { chartDataSettings } from './_constants';
import {
  WorkloadsSummaryMetricsChartDataKey,
  WorkloadsSummaryMetricsChartItem,
} from './_types';

const colorMap: Record<WorkloadsSummaryMetricsChartDataKey, string> = {
  originalReq: chartDataSettings.originalReq.color,
  req: chartDataSettings.req.color,
  rec: chartDataSettings.rec.color,
  usage: chartDataSettings.usage.color,
};

const titleMap: Record<WorkloadsSummaryMetricsChartDataKey, string> = {
  originalReq: chartDataSettings.originalReq.title,
  req: chartDataSettings.req.title,
  rec: chartDataSettings.rec.title,
  usage: chartDataSettings.usage.title,
};

const METRIC_KEYS = Object.keys(
  chartDataSettings
) as WorkloadsSummaryMetricsChartDataKey[];

export type ResourceAvgMetricsArgs = {
  toggleMetric: (key: WorkloadsSummaryMetricsChartDataKey) => void;
  isMetricDisabled: (key: WorkloadsSummaryMetricsChartDataKey) => boolean;
  unit: string;
  data: WorkloadsSummaryMetricsChartItem[] | undefined;
  isLoading?: boolean;
  testId: string;
};

export const ResourceAvgMetrics = ({
  data,
  toggleMetric,
  isMetricDisabled,
  unit,
  isLoading = false,
  testId,
}: ResourceAvgMetricsArgs) => {
  const averages = useMemo(() => {
    if (!data) {
      return;
    }

    return METRIC_KEYS.reduce((acc, key) => {
      acc[key] = round(meanBy(data, key), 2);
      return acc;
    }, {} as Record<WorkloadsSummaryMetricsChartDataKey, number>);
  }, [data]);

  if (isLoading) {
    return (
      <ChartMetricsRowSkeleton
        alwaysRow
        gap={16}
        count={METRIC_KEYS.length}
        testId={`${testId}-skeleton`}
      />
    );
  }

  return (
    <ChartMetricsRow alwaysRow alignItems="center" gap={16} testId={testId}>
      {METRIC_KEYS.map((keyCast) => {
        const settings = chartDataSettings[keyCast];

        return (
          <InventoryResourceMetricBox
            key={keyCast}
            testId={keyCast}
            dataKey={keyCast}
            value={averages?.[keyCast]}
            unit={unit}
            label={`Avg. ${settings.title}`}
            infoTooltip={settings.tooltipText}
            colorMap={colorMap}
            titleMap={titleMap}
            toggleMetric={toggleMetric}
            isMetricDisabled={isMetricDisabled}
            dashed={settings.dashed}
          />
        );
      })}
    </ChartMetricsRow>
  );
};
